/**
 * Shared bulk-action buttons for anywhere you have a list of candidate IDs
 * (CandidatesList or Job > Candidates list). Extracted from CandidatesList so
 * JobApplicantsTable can offer the same bulk operations.
 *
 * Every button takes `candidateIds: string[]` + `onDone?: () => void`.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Briefcase as BriefcaseIcon, Bookmark, Sparkles, RefreshCw } from "lucide-react";

export function BulkAssignButton({ candidateIds, onDone, testIdPrefix = "bulk", excludeJobId = null }) {
    const [open, setOpen] = useState(false);
    const [jobs, setJobs] = useState([]);
    const [picked, setPicked] = useState(new Set());
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) api.get("/jobs", { params: { status: "active" } }).then((r) => {
            // Phase 150 — when invoked from inside a job (JobApplicantsTable), hide
            // that job from the list so the recruiter isn't offered a no-op assign.
            const list = (r.data || []).filter((j) => !excludeJobId || j.id !== excludeJobId);
            setJobs(list);
        }).catch(() => {});
    }, [open, excludeJobId]);

    const submit = async () => {
        if (picked.size === 0 || candidateIds.length === 0) return;
        setBusy(true);
        try {
            const { data } = await api.post("/candidates/bulk-assign-jobs", { candidate_ids: candidateIds, job_ids: [...picked] });
            toast.success(`Created ${data.created} applications · skipped ${data.skipped}${data.failed ? ` · ${data.failed} failed` : ""}`);
            setOpen(false);
            setPicked(new Set());
            onDone && onDone();
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button disabled={candidateIds.length === 0} variant="outline" size="sm" className="rounded-none h-8" data-testid={`${testIdPrefix}-assign-btn`}>
                    <BriefcaseIcon className="w-3.5 h-3.5 mr-1.5" /> Assign · {candidateIds.length}
                </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-xl">
                <DialogHeader>
                    <DialogTitle className="font-display font-bold text-2xl">Assign {candidateIds.length} candidate{candidateIds.length === 1 ? "" : "s"} to jobs</DialogTitle>
                </DialogHeader>
                <div className="max-h-[50vh] overflow-y-auto border border-border divide-y divide-border">
                    {jobs.length === 0 ? (
                        <div className="p-6 text-sm text-muted-foreground text-center">No active jobs</div>
                    ) : jobs.map((j) => (
                        <label key={j.id} className="flex items-start gap-3 p-3 cursor-pointer hover:bg-secondary">
                            <Checkbox
                                checked={picked.has(j.id)}
                                onCheckedChange={(v) => {
                                    const s = new Set(picked);
                                    if (v) s.add(j.id); else s.delete(j.id);
                                    setPicked(s);
                                }}
                                className="rounded-none mt-1"
                                data-testid={`${testIdPrefix}-pick-job-${j.id}`}
                            />
                            <div className="flex-1">
                                <div className="font-medium text-sm">{j.title}</div>
                                <div className="text-xs text-muted-foreground font-mono">{j.location || "Remote"} · {j.applicant_count} candidates</div>
                            </div>
                        </label>
                    ))}
                </div>
                <DialogFooter>
                    <Button onClick={submit} disabled={busy || picked.size === 0} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid={`${testIdPrefix}-assign-confirm`}>
                        {busy ? "Assigning..." : `Assign · ${candidateIds.length} × ${picked.size}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function BulkAddToListButton({ candidateIds, onDone, testIdPrefix = "bulk" }) {
    const [open, setOpen] = useState(false);
    const [lists, setLists] = useState([]);
    const [picked, setPicked] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) api.get("/hiring-lists").then((r) => setLists(r.data || [])).catch(() => {});
    }, [open]);

    const submit = async () => {
        if (!picked || candidateIds.length === 0) return;
        setBusy(true);
        try {
            const { data } = await api.post("/candidates/bulk-add-to-list", { candidate_ids: candidateIds, list_id: picked });
            toast.success(`Added ${data.added} to list · skipped ${data.skipped}`);
            setOpen(false);
            setPicked(null);
            onDone && onDone();
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button disabled={candidateIds.length === 0} variant="outline" size="sm" className="rounded-none h-8" data-testid={`${testIdPrefix}-list-btn`}>
                    <Bookmark className="w-3.5 h-3.5 mr-1.5" /> List · {candidateIds.length}
                </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display font-bold text-2xl">Add {candidateIds.length} to hiring list</DialogTitle>
                </DialogHeader>
                <div className="border border-border divide-y divide-border max-h-64 overflow-y-auto">
                    {lists.length === 0 ? (
                        <div className="p-6 text-sm text-muted-foreground text-center">No lists yet — create one in Lists</div>
                    ) : lists.map((l) => (
                        <label key={l.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-secondary">
                            <input
                                type="radio"
                                name={`${testIdPrefix}-list`}
                                checked={picked === l.id}
                                onChange={() => setPicked(l.id)}
                                data-testid={`${testIdPrefix}-pick-list-${l.id}`}
                            />
                            <div className="flex-1 text-sm">
                                <div className="font-medium">{l.name}</div>
                                <div className="text-xs text-muted-foreground">{l.member_count} candidates</div>
                            </div>
                        </label>
                    ))}
                </div>
                <DialogFooter>
                    <Button onClick={submit} disabled={busy || !picked} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid={`${testIdPrefix}-list-confirm`}>{busy ? "Adding..." : "Add"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function BulkAnalyzeButton({ candidateIds, onDone, testIdPrefix = "bulk" }) {
    const [busy, setBusy] = useState(false);
    const run = async () => {
        if (candidateIds.length === 0) return;
        if (!window.confirm(`Run AI analysis on ${candidateIds.length} selected candidate${candidateIds.length === 1 ? "" : "s"}? This will charge the AI budget for each parse.`)) return;
        setBusy(true);
        let ok = 0, fail = 0;
        const queue = [...candidateIds];
        const worker = async () => {
            while (queue.length) {
                const cid = queue.shift();
                try { await api.post(`/candidates/${cid}/reparse`); ok += 1; }
                catch { fail += 1; }
            }
        };
        try {
            await Promise.all([worker(), worker(), worker()]);  // 3 concurrent
            toast.success(`AI analysis queued — ${ok} started${fail ? `, ${fail} failed` : ""}`);
            onDone?.();
        } finally { setBusy(false); }
    };
    return (
        <Button
            onClick={run}
            disabled={busy || candidateIds.length === 0}
            variant="outline"
            size="sm"
            className="rounded-none h-8 border-ikb/40 text-ikb hover:bg-ikb/5"
            data-testid={`${testIdPrefix}-analyze-btn`}
            title="Run AI analysis on the selected candidates — uses your AI budget"
        >
            <Sparkles className="w-3.5 h-3.5 mr-1" />
            {busy ? "Running…" : `Run AI · ${candidateIds.length}`}
        </Button>
    );
}

export function BulkRetryButton({ candidateIds, items = [], onDone, testIdPrefix = "bulk" }) {
    const [busy, setBusy] = useState(false);
    // How many of the picked rows are in a state where retry makes sense
    const retryable = (() => {
        if (candidateIds.length === 0) return 0;
        const picked = new Set(candidateIds);
        return items.filter((c) => picked.has(c.id) && ["failed", "ai_unavailable"].includes(c.processing_status)).length;
    })();
    const run = async () => {
        if (candidateIds.length === 0) return;
        if (!window.confirm(`Retry parsing for ${candidateIds.length} selected candidate${candidateIds.length === 1 ? "" : "s"}? Rows without a resume on file will be skipped.`)) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/candidates/bulk-reparse`, { candidate_ids: candidateIds });
            const queued = data.queued || 0;
            const skipped = (data.skipped || []).length;
            if (queued > 0) toast.success(`Re-parsing ${queued} candidate${queued === 1 ? "" : "s"}${skipped ? ` · ${skipped} skipped` : ""} — track progress in the AI Queue widget`);
            else toast.error(skipped ? `All ${skipped} candidate${skipped === 1 ? "" : "s"} skipped (no resume on file)` : "Nothing queued");
            onDone?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <Button
            onClick={run}
            disabled={busy || candidateIds.length === 0}
            variant="outline"
            size="sm"
            className="rounded-none h-8 border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid={`${testIdPrefix}-retry-btn`}
            title={retryable > 0 ? `${retryable} of the selected are failed/paused and will retry — others will re-run too if they have resumes on file` : "Re-runs AI parsing for all selected candidates with a resume on file"}
        >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Retrying…" : `Retry · ${candidateIds.length}`}
        </Button>
    );
}
